import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useAuth } from '../auth/AuthProvider.jsx'
import { usePushNotification } from '../hooks/usePushNotification.js'
import { canAccessSettings, canEditCatalog, canMoveStock, canViewReports, canAccessCompras, canViewHistoricoCompras, canCreatePedidos, canViewEstoque, PERFIS, getMenuByProfile } from '../utils/permissions.js'
import { forceRefresh, fetchProdutos } from '../data/syncManager.js'

function isComprador(perfil) {
  return perfil === PERFIS.comprador
}

export default function Shell({ children }) {
  const [menuAberto, setMenuAberto] = useState(false)
  const [alertasEstoque, setAlertasEstoque] = useState(0)
  const [syncLoading, setSyncLoading] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { logout, session, exitEmpresa } = useAuth()
  const perfil = session?.perfil
  usePushNotification(session)


  const handleForceSync = async () => {
    if (syncLoading) return
    setSyncLoading(true)
    console.log('[ONLINE] Forçando atualização de dados...')
    try {
      await forceRefresh()
      console.log('[ONLINE] Atualização concluída')
      // Recarregar página para atualizar estado
      window.location.reload()
    } catch (err) {
      console.error('[ONLINE] Erro ao atualizar:', err)
    } finally {
      setSyncLoading(false)
    }
  }

  useEffect(() => {
    if (!session?.empresaId) return  // super admin sem empresa não busca produtos
    async function checkAlertas() {
      try {
        const produtos = await fetchProdutos()
        const alertas = produtos.filter(p => (p.quantidade ?? 0) < (p.quantidadeMin ?? p.estoqueMinimo ?? 0) && (p.quantidadeMin ?? p.estoqueMinimo ?? 0) > 0)
        setAlertasEstoque(alertas.length)
      } catch {
        setAlertasEstoque(0)
      }
    }
    checkAlertas()
    const interval = setInterval(checkAlertas, 60000) // Verifica a cada 60s
    return () => clearInterval(interval)
  }, [session?.empresaId])

  const isEstoque = location.pathname === '/app/estoque' || location.pathname === '/app/funcionarios'
  const isMais = ['/app/pedidos', '/app/relatorios', '/app/config', '/app/auditoria', '/app/usuarios'].some(p => location.pathname.startsWith(p))

  const navLinkClass = (isActive) =>
    `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? 'bg-white/15 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'}`

  async function handleLogout() {
    setMenuAberto(false)
    try { await logout() } catch (e) { console.warn('[Shell] Erro no logout:', e) }
    if (typeof window !== 'undefined') window.currentSession = null
    sessionStorage.clear()
    // Voltar para tela de login
    navigate('/login')
  }

  return (
    <div className="relative h-screen w-screen overflow-hidden text-white">
      <div className="fixed inset-0 z-0 cm-bg" />
      <div className="fixed inset-0 z-0 cm-bg-overlay" />

      {/* Banner de impersonação */}
      {session?._impersonating && (
        <div className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between gap-2 bg-amber-500 px-3 py-1 text-[10px] font-semibold text-black">
          <span>👁️ {session.empresaNome}</span>
          <button
            onClick={() => { exitEmpresa(); navigate('/super-admin') }}
            className="rounded px-2 py-0.5 bg-black/20 hover:bg-black/30"
          >
            ← Voltar
          </button>
        </div>
      )}

      {/* ── LAYOUT PC (lg+): sidebar esquerda + conteúdo direita ── */}
      <div className={`relative z-10 hidden h-full lg:flex ${session?._impersonating ? 'pt-8' : ''}`}>

        {/* Sidebar */}
        <aside className="flex w-56 shrink-0 flex-col border-r border-white/10 bg-black/20 px-3 py-6 backdrop-blur overflow-y-auto">
          {/* Logo + Sync */}
          <div className="mb-6 px-2">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 overflow-hidden rounded-xl bg-white/10">
                <img src="/logo.png" alt="CAIXA MESTRE" className="h-full w-full object-contain" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold tracking-wide">CAIXA MESTRE</div>
                <div className="text-[10px] text-white/60">Gestão de Estoque</div>
              </div>
            </div>
            {perfil === PERFIS.admin && (
              <button
                onClick={handleForceSync}
                disabled={syncLoading}
                className="mt-3 w-full rounded-lg bg-white/10 px-2 py-1.5 text-[10px] font-semibold text-white/80 disabled:opacity-50 hover:bg-white/20"
              >
                {syncLoading ? '🔄 Sincronizando...' : '🔄 Sincronizar'}
              </button>
            )}
          </div>

          {/* Nav links por perfil */}
          <nav className="flex flex-col gap-1 flex-1 min-h-0">
            {!isComprador(perfil) && (
              <>
                {/* ── GRUPO 1: OPERAÇÕES DIÁRIAS ── */}
                <NavLink to="/app" end className={({ isActive }) => navLinkClass(isActive)}>
                  <span className="text-base">🏠</span><span>Início</span>
                </NavLink>
                <NavLink to="/app/entrega" className={({ isActive }) => navLinkClass(isActive)}>
                  <span className="text-base">🚚</span><span>Entrega</span>
                </NavLink>
                <NavLink to="/app/obras" className={({ isActive }) => navLinkClass(isActive)}>
                  <span className="text-base">🏗️</span><span>Obras</span>
                </NavLink>
                <NavLink to="/app/estoque" className={() => navLinkClass(isEstoque)}>
                  <span className="text-base">📦</span>
                  <span className="flex-1">Estoque</span>
                  {alertasEstoque > 0 && (
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold">
                      {alertasEstoque > 9 ? '9+' : alertasEstoque}
                    </span>
                  )}
                </NavLink>
                <NavLink to="/app/pendentes" className={({ isActive }) => navLinkClass(isActive)}>
                  <span className="text-base">⏰</span><span>Pendentes</span>
                </NavLink>

                <div className="my-2 border-t border-white/10" />

                {/* ── GRUPO 2: PROCESSOS ── */}
                {canAccessCompras(perfil) && (
                  <NavLink to="/app/compras" className={({ isActive }) => navLinkClass(isActive)}>
                    <span className="text-base">🛒</span><span>Compras</span>
                  </NavLink>
                )}
                {canCreatePedidos(perfil) && (
                  <NavLink to="/app/pedidos" className={({ isActive }) => navLinkClass(isActive)}>
                    <span className="text-base">📋</span><span>Pedidos</span>
                  </NavLink>
                )}

                <div className="my-2 border-t border-white/10" />

                {/* ── GRUPO 3: GERENCIAL ── */}
                {perfil === 'admin' && (
                  <NavLink to="/app/financeiro" className={({ isActive }) => navLinkClass(isActive)}>
                    <span className="text-base">💰</span><span>Financeiro</span>
                  </NavLink>
                )}
                {canViewReports(perfil) && (
                  <NavLink to="/app/relatorios" className={({ isActive }) => navLinkClass(isActive)}>
                    <span className="text-base">📊</span><span>Relatórios</span>
                  </NavLink>
                )}
                {canAccessSettings(perfil) && (
                  <NavLink to="/app/configuracoes" className={({ isActive }) => navLinkClass(isActive)}>
                    <span className="text-base">⚙️</span><span>Configurações</span>
                  </NavLink>
                )}

                <div className="my-2 border-t border-white/10" />

                {/* ── GRUPO 4: INFO ── */}
                <NavLink to="/app/sobre" className={({ isActive }) => navLinkClass(isActive)}>
                  <span className="text-base">ℹ️</span><span>Sobre</span>
                </NavLink>
              </>
            )}
            {isComprador(perfil) && (
              <>
                <NavLink to="/app/compras" className={({ isActive }) => navLinkClass(isActive)}>
                  <span className="text-base">🛒</span><span>Compras</span>
                </NavLink>
                <NavLink to="/app/estoque" className={() => navLinkClass(isEstoque)}>
                  <span className="text-base">📦</span>
                  <span className="flex-1">Estoque</span>
                  {alertasEstoque > 0 && (
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold">
                      {alertasEstoque > 9 ? '9+' : alertasEstoque}
                    </span>
                  )}
                </NavLink>
                <NavLink to="/app/historico-compras" className={({ isActive }) => navLinkClass(isActive)}>
                  <span className="text-base">📜</span><span>Histórico</span>
                </NavLink>
                <NavLink to="/app/sobre" className={({ isActive }) => navLinkClass(isActive)}>
                  <span className="text-base">ℹ️</span><span>Sobre</span>
                </NavLink>
              </>
            )}
          </nav>

          {/* Usuário + logout */}
          <div className="mt-auto border-t border-white/10 pt-4">
            <div className="mb-1 px-2 text-[10px] text-white/40">{session?.perfil}</div>
            <div className="mb-2 px-2 text-xs font-medium text-white/60 truncate">{session?.nome || session?.email}</div>
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-red-300 hover:bg-red-500/10"
            >
              <span className="text-base">🚪</span><span>Sair</span>
            </button>
          </div>
        </aside>

        {/* Conteúdo */}
        <main className="flex-1 overflow-y-auto px-6 py-6">
          <div className="mx-auto max-w-4xl">
            {children || <Outlet />}
          </div>
        </main>
      </div>

      {/* ── LAYOUT MOBILE (< lg): coluna única + navbar bottom ── */}
      <div className="relative z-10 flex h-full flex-col lg:hidden">
        <div className="relative z-10 mx-auto flex h-full w-full max-w-lg flex-col">
          <header className="shrink-0 px-4 pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 overflow-hidden rounded-xl bg-white/10">
                <img src="/logo.png" alt="CAIXA MESTRE" className="h-full w-full object-contain" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-semibold tracking-wide">CAIXA MESTRE</div>
                  {perfil === PERFIS.admin && (
                    <button
                      onClick={handleForceSync}
                      disabled={syncLoading}
                      className="rounded-lg bg-white/10 px-2 py-1 text-[10px] font-semibold text-white/80 disabled:opacity-50"
                    >
                      {syncLoading ? '🔄...' : '🔄 Sincronizar'}
                    </button>
                  )}
                </div>
                <div className="text-xs text-white/70">Gestão de Estoque</div>
              </div>
            </div>
          </header>

          <main className="flex-1 overflow-y-auto px-4 pb-28 pt-6">
            {children || <Outlet />}
          </main>

          {menuAberto && (
            <div
              className="fixed inset-0 z-40 flex items-end justify-center bg-black/50"
              onClick={() => setMenuAberto(false)}
            >
              <div className="mb-16 w-full max-w-lg px-4" onClick={e => e.stopPropagation()}>
                <div className="rounded-xl bg-brand-900 p-2 shadow-xl">
                  <div className="grid grid-cols-2 gap-1">
                    {/* ── GRUPO 2: PROCESSOS ── */}
                    {canAccessCompras(perfil) && !isComprador(perfil) && (
                      <NavLink to="/app/compras" onClick={() => setMenuAberto(false)} className="flex flex-col items-center justify-center rounded-lg bg-white/10 p-2 text-center text-[10px]">
                        <span className="mb-1 text-lg">🛒</span><span>Compras</span>
                      </NavLink>
                    )}
                    {canCreatePedidos(perfil) && !isComprador(perfil) && (
                      <NavLink to="/app/pedidos" onClick={() => setMenuAberto(false)} className="flex flex-col items-center justify-center rounded-lg bg-white/10 p-2 text-center text-[10px]">
                        <span className="mb-1 text-lg">📋</span><span>Pedidos</span>
                      </NavLink>
                    )}
                    {/* ── GRUPO 3: GERENCIAL ── */}
                    {perfil === 'admin' && (
                      <NavLink to="/app/financeiro" onClick={() => setMenuAberto(false)} className="flex flex-col items-center justify-center rounded-lg bg-white/10 p-2 text-center text-[10px]">
                        <span className="mb-1 text-lg">💰</span><span>Financeiro</span>
                      </NavLink>
                    )}
                    {canViewReports(perfil) && !isComprador(perfil) && (
                      <NavLink to="/app/relatorios" onClick={() => setMenuAberto(false)} className="flex flex-col items-center justify-center rounded-lg bg-white/10 p-2 text-center text-[10px]">
                        <span className="mb-1 text-lg">📊</span><span>Relatórios</span>
                      </NavLink>
                    )}
                    {canAccessSettings(perfil) && (
                      <NavLink to="/app/configuracoes" onClick={() => setMenuAberto(false)} className="flex flex-col items-center justify-center rounded-lg bg-white/10 p-2 text-center text-[10px]">
                        <span className="mb-1 text-lg">⚙️</span><span>Configurações</span>
                      </NavLink>
                    )}
                    {/* ── GRUPO 4: INFO ── */}
                    {!isComprador(perfil) && (
                      <NavLink to="/app/sobre" onClick={() => setMenuAberto(false)} className="flex flex-col items-center justify-center rounded-lg bg-white/10 p-2 text-center text-[10px]">
                        <span className="mb-1 text-lg">ℹ️</span><span>Sobre</span>
                      </NavLink>
                    )}
                  </div>
                  <button onClick={handleLogout} className="mt-2 w-full rounded-lg bg-red-500/20 py-2 text-[10px] text-red-300 font-semibold">
                    🚪 Sair do App
                  </button>
                  <button onClick={() => setMenuAberto(false)} className="mt-1 w-full rounded-lg bg-white/5 py-1.5 text-[10px] text-white/70">
                    ✕ Fechar
                  </button>
                </div>
              </div>
            </div>
          )}

          <nav className="fixed bottom-0 left-0 right-0 border-t border-white/10 bg-brand-900/95 backdrop-blur">
            <div className={`mx-auto grid max-w-md ${isComprador(perfil) ? 'grid-cols-4' : 'grid-cols-6'} px-1 py-2 text-xs`}>
              {!isComprador(perfil) && (
                <>
                  {/* ── GRUPO 1: OPERAÇÕES DIÁRIAS ── */}
                  <NavLink to="/app" end className={({ isActive }) => `flex flex-col items-center justify-center rounded-xl px-1 py-2 ${isActive ? 'bg-white/10 text-white' : 'text-white/70'}`}>
                    <span className="text-lg">🏠</span><span>Início</span>
                  </NavLink>
                  <NavLink to="/app/entrega" className={({ isActive }) => `flex flex-col items-center justify-center rounded-xl px-1 py-2 ${isActive ? 'bg-white/10 text-white' : 'text-white/70'}`}>
                    <span className="text-lg">🚚</span><span>Entrega</span>
                  </NavLink>
                  <NavLink to="/app/obras" className={({ isActive }) => `flex flex-col items-center justify-center rounded-xl px-1 py-2 ${isActive ? 'bg-white/10 text-white' : 'text-white/70'}`}>
                    <span className="text-lg">🏗️</span><span>Obras</span>
                  </NavLink>
                  <NavLink to="/app/estoque" className={() => `relative flex flex-col items-center justify-center rounded-xl px-1 py-2 ${isEstoque ? 'bg-white/10 text-white' : 'text-white/70'}`}>
                    <span className="text-lg">📦</span>
                    <span>Estoque</span>
                    {alertasEstoque > 0 && (
                      <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[8px] font-bold">
                        {alertasEstoque > 9 ? '9+' : alertasEstoque}
                      </span>
                    )}
                  </NavLink>
                  <NavLink to="/app/pendentes" className={({ isActive }) => `flex flex-col items-center justify-center rounded-xl px-1 py-2 ${isActive ? 'bg-white/10 text-white' : 'text-white/70'}`}>
                    <span className="text-lg">⏰</span><span>Pendentes</span>
                  </NavLink>
                </>
              )}
              {isComprador(perfil) && (
                <>
                  <NavLink to="/app/compras" className={({ isActive }) => `flex flex-col items-center justify-center rounded-xl px-1 py-2 ${isActive ? 'bg-white/10 text-white' : 'text-white/70'}`}>
                    <span className="text-lg">🛒</span><span>Compras</span>
                  </NavLink>
                  <NavLink to="/app/estoque" className={() => `relative flex flex-col items-center justify-center rounded-xl px-1 py-2 ${isEstoque ? 'bg-white/10 text-white' : 'text-white/70'}`}>
                    <span className="text-lg">📦</span>
                    <span>Estoque</span>
                    {alertasEstoque > 0 && (
                      <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[8px] font-bold">
                        {alertasEstoque > 9 ? '9+' : alertasEstoque}
                      </span>
                    )}
                  </NavLink>
                  <NavLink to="/app/sobre" className={({ isActive }) => `flex flex-col items-center justify-center rounded-xl px-1 py-2 ${isActive ? 'bg-white/10 text-white' : 'text-white/70'}`}>
                    <span className="text-lg">ℹ️</span><span>Sobre</span>
                  </NavLink>
                </>
              )}
              <button onClick={() => setMenuAberto(true)} className={`flex flex-col items-center justify-center rounded-xl px-1 py-2 ${isMais ? 'bg-white/10 text-white' : 'text-white/70'}`}>
                <span className="text-lg">⋮⋮⋮</span><span>Mais</span>
              </button>
            </div>
          </nav>
        </div>
      </div>
    </div>
  )
}
